import { usePostHog } from 'posthog-js/react';
import dayjs from 'dayjs';

const disabledPosthog = process.env.NEXT_PUBLIC_DISABLED_POSTHOG
  ? process.env.NEXT_PUBLIC_DISABLED_POSTHOG === 'true'
  : false;

export default function useAnalyticsEvents() {
  const posthog = usePostHog();

  const sendEventToPosthog = (event, data) => {
    if (disabledPosthog) return;

    if (data) {
      posthog?.capture(event, data);
    } else {
      posthog?.capture(event);
    }
  };

  const sendEventToGTM = (event, path, data) => {
    if (!window.dataLayer) return;

    window.dataLayer.push({
      event,
      path,
      ...(data ? data : {}),
    });
  };

  const sendEventToFB = (event, data) => {
    if (!window.fbq) return;

    if (data) {
      window.fbq('track', event, data);
    } else {
      window.fbq('track', event);
    }
  };

  /** Main Events */

  const addToCart = (data) => {
    sendEventToGTM('addToCart', '/auth', data);
    sendEventToFB('AddToCart', data);
  };

  const pageview = (url) => {
    sendEventToGTM('pageview', url);
    sendEventToFB('PageView');
  };

  const purchase = (data) => {
    // sendEventToPosthog('purchase', data);
    sendEventToGTM('purchase', '/success', { ecommerce: data });
    sendEventToFB('Purchase', data);
  };

  const payment = (data) => {
    sendEventToGTM('begin_checkout', '/payment', { ecommerce: data });
    sendEventToPosthog('payment', { $payment: data });
    sendEventToFB('InitiateCheckout', data);
  };

  const reserveTickets = (data) => {
    sendEventToGTM('reserveTickets', '/payment', { ecommerce: data });
    sendEventToFB('Lead', data);
  };

  /** End Main Events */

  const increaseNumberOfTickets = (quantity) =>
    sendEventToPosthog('increaseNumberOfTickets', {
      $ticketsQuantity: quantity,
    });
  const decreaseNumberOfTickets = (quantity) =>
    sendEventToPosthog('increaseNumberOfTickets', {
      $ticketsQuantity: quantity,
    });
  const orderByPrice = () => sendEventToPosthog('orderByPrice');
  const showEventDetails = () => sendEventToPosthog('showEventDetails');
  const ticketSelectedFromList = (quantity, ticket) =>
    sendEventToPosthog('ticketSelectedFromList', {
      $ticketsQuantity: quantity,
      $ticket: ticket,
    });
  const clickOnDeleteTicketFromList = (ticket) =>
    sendEventToPosthog('clickOnDeleteTicketFromList', {
      $ticket: ticket,
    });
  const cancelDeleteTicketFromList = (ticket) =>
    sendEventToPosthog('cancelDeleteTicketFromList', {
      $ticket: ticket,
    });
  const ticketRemovedFromList = (ticket) =>
    sendEventToPosthog('ticketRemovedFromList', {
      $ticket: ticket,
    });
  const ticketSelectedFromMap = (ticket) =>
    sendEventToPosthog('ticketSelectedFromMap', {});
  const ticketRemovedFromMap = (ticket) =>
    sendEventToPosthog('ticketRemovedFromMap', {});
  const showTicketTypeDetails = (ticket) =>
    sendEventToPosthog('showTicketTypeDetails', {
      $ticket: ticket,
    });
  const addTicketTypeQuantity = (quantity, ticket) =>
    sendEventToPosthog('addTicketTypeQuantity', {
      $ticketsQuantity: quantity,
      $ticket: ticket,
    });
  const removeTicketType = (ticket) =>
    sendEventToPosthog('removeTicketType', {
      $ticket: ticket,
    });
  const showCartDetails = () => sendEventToPosthog('showCartDetails');
  const buyTickets = (order, booking) =>
    sendEventToPosthog('buyTickets', { $order: order, $booking: booking });
  const clickedOnMap = () => sendEventToPosthog('clickedOnMap');
  const clickedOnBackButton = () => sendEventToPosthog('clickedOnBackButton');
  const clickedOnPromotionButton = () =>
    sendEventToPosthog('clickedOnPromotionButton');
  const promotionAdded = (cupon) =>
    sendEventToPosthog('promotionAdded', { $cupon: cupon });
  const promotionError = (cupon) =>
    sendEventToPosthog('promotionError', { $cupon: cupon });
  const removePromotion = (cupon) =>
    sendEventToPosthog('removePromotion', { $cupon: cupon });
  const clearTickets = () => sendEventToPosthog('clearTickets');
  const termsAndConditions = () => sendEventToPosthog('termsAndConditions');
  const shoppingTerms = () => sendEventToPosthog('shoppingTerms');
  const noticeOfPrivacy = () => sendEventToPosthog('noticeOfPrivacy');
  const help = () => sendEventToPosthog('help');
  const timeExpired = () => sendEventToPosthog('timeExpired');
  const reloadForTimeExpired = () => sendEventToPosthog('reloadForTimeExpired');
  const signUp = (user) => sendEventToPosthog('signUp', { $user: user });
  const signUpGuest = (user) =>
    sendEventToPosthog('signUpGuest', { $user: user });
  const signIn = (user) => sendEventToPosthog('signIn', { $user: user });
  const verifyUser = (user) =>
    sendEventToPosthog('verifyUser', { $user: user });
  const verifyUserError = (user) =>
    sendEventToPosthog('verifyUserError', { $user: user });
  const resendCode = (user) =>
    sendEventToPosthog('resendCode', { $user: user });

  const editNames = () => sendEventToPosthog('editNames');
  const applyNameToAllTicket = () => sendEventToPosthog('applyNameToAllTicket');
  const sumbmitTicketForm = () => sendEventToPosthog('sumbmitTicketForm');

  const changePaymentMethod = (paymentMethod) =>
    sendEventToPosthog('changePaymentMethod', {
      $paymentMethod: paymentMethod,
    });

  const showMoreEvents = () => sendEventToPosthog('showMoreEvents');
  const buyMore = () => sendEventToPosthog('buyMore');

  const saveSurvey = (survey) =>
    sendEventToPosthog('saveSurvey', { $survey: survey });
  const saveCustomTickets = (survey) =>
    sendEventToPosthog('saveCustomTickets', { $survey: survey });

  // Insurance events

  const insuranceProtectionActivated = () => {
    sendEventToPosthog('insurance_protection_activated', {
      step_name: 'protege_tus_boletos',
      insurance_status: 'activated',
    });
  };
  const insuranceProtectionDeactivated = () => {
    sendEventToPosthog('insurance_protection_deactivated', {
      step_name: 'protege_tus_boletos',
      insurance_status: 'deactivated',
    });
  };
  const insuranceTermsViewed = () => {
    sendEventToPosthog('insurance_terms_viewed', {
      step_name: 'protege_tus_boletos',
      insurance_status: 'insurance_terms_viewed',
      insurance_terms_clicked: true,
    });
  };
  const insuranceRemovedFromSummary = () => {
    sendEventToPosthog('insurance_removed_from_summary', {
      step_name: 'paga_tus_boletos',
      insurance_status: 'removed_summary',
    });
  };
  const insuranceReactivatedInSummary = () => {
    sendEventToPosthog('insurance_reactivated_in_summary', {
      step_name: 'paga_tus_boletos',
      insurance_status: 'reactivated_summary',
    });
  };
  const insuranceSkipped = () => {
    sendEventToPosthog('insurance_skipped', {
      step_name: 'protege_tus_boletos',
      insurance_status: 'insurance_skipped',
    });
  };

  const insuranceEneabled = (data) => {
    sendEventToPosthog('insurance_eneabled', data);
  };

  // Merch events

  const eventDetailView = (data) => {
    const date = dayjs();
    sendEventToPosthog('eventdetail_view', {
      id: data,
      date,
    });
  };

  const eventDetailPurchaseWithMerch = (data) => {
    sendEventToPosthog('eventdetail_purchase_with_merch', {
      tickets_qty: data.ticktes,
      combos_qty: data.withMerch,
    });
  };

  const eventDetailPurchaseNoMerch = (data) => {
    sendEventToPosthog('eventdetail_purchase_no_merch', {
      tickets_qty: data.tickets,
      event_with_merch: data.eventWithMerch,
    });
  };

  const merchSizeView = () => {
    sendEventToPosthog('merchsize_view');
  };

  const merchSizeComplete = () => {
    sendEventToPosthog('merchsize_complete');
  };

  const merchShippingView = (data) => {
    sendEventToPosthog('merchshipping_view', {
      shipping_qty: data,
    });
  };

  const merchShippngComplete = (data) => {
    const date = dayjs();
    sendEventToPosthog('merchshipping_complete', {
      shipping_form_complete: true,
      postal_code: data,
      date,
    });
  };

  // secondary market
  //
  const secondaryMarketTiketsAvailable = (data) => {
    sendEventToPosthog('CTA_impresion_BoletosFanAFan_LandingEvento', data);
  };

  const secondaryMarketClick = (data) => {
    sendEventToPosthog('CTA_Click_BoletosFanAFan');
  };

  // msci events

  const cardNotAvailbleForInstallments = () => {
    sendEventToPosthog('card_validated_no_elegible_for_installments');
  };

  const cardAvailableForInstallments = () => {
    sendEventToPosthog('card_validated_elegible_for_installments');
  };

  // Apertura del selector

  const msciSelectorOpened = () => {
    sendEventToPosthog('installment_selector_opened');
  };

  // Selección de una opción

  /**
   * @typedef {Object} InstallmentsOptionData
   * @property {'contado' | 'meses_sin_intereses' | 'meses_con_intereses'} payment_type - tipo de modalidad seleccionada msi | mci | contado
   * @property {number} installments_count - plan de pago seleccionado { 3, 6, 9, 12 }, si es de contado se envía 0
   */

  /**
   * @param { InstallmentsOptionData } installmentsOptionsData
   * @returns void
   */

  const paymentOptionSelected = (installmentsOptionsData) => {
    sendEventToPosthog('installments_option_selected', installmentsOptionsData);
  };

  // Selector deshabilitado por monto

  const instalmentsSelectorDisabled = () => {
    sendEventToPosthog('installments_selector_disabled_by_amount');
  };

  // requiere nueva selección

  const newSelectionIsRequiered = () => {
    sendEventToPosthog('installment_selector_requires_reselect');
  };
  // billboards shorten template

  const billboardViewed = () => {
    sendEventToPosthog('cartelera_viewed');
  };

  const selectorOpened = () => {
    sendEventToPosthog('selector_opened');
  };

  const dateSelected = (date, dayOfWeek) => {
    sendEventToPosthog('date_selected', {
      date,
      day_of_week: dayOfWeek,
    });
  };

  const timeSelected = (date, time) => {
    sendEventToPosthog('time_selected', {
      date,
      time,
    });
  };

  const viewTickets = (date, time) => {
    sendEventToPosthog('cta_ver_boletos_click', {
      date,
      time,
    });
  };

  const selectorClosed = () => {
    sendEventToPosthog('selector_closed');
  };

  const calendarMonthChanged = (month, year) => {
    sendEventToPosthog('calendar_month_changed', { month, year });
  };

  return {
    sendEventToPosthog,
    billboardViewed,
    selectorOpened,
    dateSelected,
    timeSelected,
    viewTickets,
    selectorClosed,
    calendarMonthChanged,
    addToCart,
    pageview,
    purchase,
    payment,
    reserveTickets,
    increaseNumberOfTickets,
    decreaseNumberOfTickets,
    orderByPrice,
    showEventDetails,
    showTicketTypeDetails,
    ticketSelectedFromList,
    clickOnDeleteTicketFromList,
    cancelDeleteTicketFromList,
    ticketRemovedFromList,
    ticketSelectedFromMap,
    ticketRemovedFromMap,
    addTicketTypeQuantity,
    removeTicketType,
    showCartDetails,
    buyTickets,
    clickedOnMap,
    clickedOnBackButton,
    clickedOnPromotionButton,
    promotionAdded,
    promotionError,
    removePromotion,
    clearTickets,
    termsAndConditions,
    shoppingTerms,
    noticeOfPrivacy,
    help,
    timeExpired,
    reloadForTimeExpired,
    signUp,
    signUpGuest,
    signIn,
    verifyUser,
    verifyUserError,
    resendCode,
    editNames,
    applyNameToAllTicket,
    sumbmitTicketForm,
    changePaymentMethod,
    showMoreEvents,
    buyMore,
    saveSurvey,
    saveCustomTickets,
    insuranceProtectionActivated,
    insuranceProtectionDeactivated,
    insuranceTermsViewed,
    insuranceRemovedFromSummary,
    insuranceReactivatedInSummary,
    insuranceSkipped,
    insuranceEneabled,
    eventDetailPurchaseWithMerch,
    eventDetailPurchaseNoMerch,
    merchSizeView,
    merchSizeComplete,
    merchShippingView,
    merchShippngComplete,
    eventDetailView,
    secondaryMarketTiketsAvailable,
    secondaryMarketClick,
    cardNotAvailbleForInstallments,
    cardAvailableForInstallments,
    msciSelectorOpened,
    paymentOptionSelected,
    instalmentsSelectorDisabled,
    newSelectionIsRequiered,
  };
}
